import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { PoolStatus, RateUnit, type RateUnit as RateUnitCode } from '@clawhire/shared';
import { api } from '@/lib/api';
import { useDebouncedValue } from '@/lib/use-debounce';
import { cn } from '@/lib/utils';
import { enumOptions, formatRate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/select';
import { toast } from '@/components/ui/toaster';
import { EnumBadge } from '@/components/data/EnumBadge';
import { useCandidateSearch, useInvalidatePositions } from '../queries';
import type { CandidateListItem, PositionDetail, SubmissionCreateResponse } from '../types';

export interface ProposeCandidateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  position: PositionDetail;
}

function displayName(c: CandidateListItem): string {
  return c.computed?.displayName ?? c.label ?? c.fullName ?? c.candidateId;
}

/** Candidate search + rates → `POST /submissions`; rate warnings are shown as toasts. */
export function ProposeCandidateDialog({ open, onOpenChange, position }: ProposeCandidateDialogProps) {
  const invalidate = useInvalidatePositions();
  const [search, setSearch] = React.useState('');
  const debounced = useDebouncedValue(search, 250);
  const candidates = useCandidateSearch(debounced, open);
  const [selected, setSelected] = React.useState<CandidateListItem | null>(null);
  const [rate, setRate] = React.useState('');
  const [unit, setUnit] = React.useState<RateUnitCode>('HOUR');
  const proposed = React.useMemo(
    () => new Set(position.submissions.map((s) => s.candidateId)),
    [position.submissions],
  );

  React.useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(null);
      setRate('');
      setUnit('HOUR');
    }
  }, [open]);

  const propose = useMutation({
    mutationFn: () => {
      const n = rate.trim() === '' ? undefined : Number(rate.replace(',', '.'));
      return api.post<SubmissionCreateResponse>('/submissions', {
        candidate: selected?.id,
        position: position.id,
        candidateRateEur: n,
        candidateRateUnit: n !== undefined ? unit : undefined,
      });
    },
    onSuccess: async (res) => {
      toast.success(`${selected ? displayName(selected) : 'Candidate'} proposed for ${position.requestNo}`);
      for (const w of res.warnings ?? []) toast.warning(w.message);
      for (const h of res.hints ?? []) toast.info(h);
      await invalidate();
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rateNumber = rate.trim() === '' ? null : Number(rate.replace(',', '.'));
  const rateValid = rateNumber === null || (Number.isFinite(rateNumber) && rateNumber >= 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (selected && rateValid) propose.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Propose a candidate</DialogTitle>
            <DialogDescription>
              {position.requestNo} · {position.role}. The submission starts at Screening; the client offer is
              suggested from the candidate rate and the effective margin.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="propose-search">Candidate</Label>
            <div className="rounded-md border">
              <Command shouldFilter={false} label="Candidate search">
                <CommandInput
                  id="propose-search"
                  placeholder="Search by name, ID, skill…"
                  value={search}
                  onValueChange={setSearch}
                />
                <CommandList className="max-h-56">
                  <CommandEmpty>{candidates.isLoading ? 'Searching…' : 'No candidates found.'}</CommandEmpty>
                  <CommandGroup>
                    {(candidates.data ?? []).map((c) => {
                      const already = proposed.has(c.id);
                      const isSelected = selected?.id === c.id;
                      return (
                        <CommandItem
                          key={c.id}
                          value={c.id}
                          disabled={already}
                          onSelect={() => {
                            setSelected(c);
                            if (c.rateExpectationEurH !== null && rate === '') {
                              setRate(String(c.rateExpectationEurH));
                              setUnit('HOUR');
                            }
                          }}
                          aria-selected={isSelected}
                          role="option"
                          className={cn(isSelected && 'bg-accent')}
                        >
                          <span
                            className={cn(
                              'flex h-4 w-4 items-center justify-center',
                              !isSelected && 'invisible',
                            )}
                            aria-hidden="true"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </span>
                          <span className="min-w-0 flex-1 truncate">
                            <span className="font-medium">{displayName(c)}</span>
                            <span className="ml-2 font-mono text-xs text-muted-foreground">
                              {c.candidateId}
                            </span>
                            {c.headline ? (
                              <span className="ml-2 text-xs text-muted-foreground">{c.headline}</span>
                            ) : null}
                          </span>
                          <EnumBadge def={PoolStatus} value={c.poolStatus} size="sm" />
                          {already ? (
                            <span className="text-xs text-muted-foreground">already proposed</span>
                          ) : null}
                          {c.rateExpectationEurH !== null ? (
                            <span className="text-xs tabular-nums text-muted-foreground">
                              {formatRate(c.rateExpectationEurH)}
                            </span>
                          ) : null}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </div>
            {selected ? (
              <p className="text-sm">
                Selected: <span className="font-medium">{displayName(selected)}</span>{' '}
                <span className="font-mono text-xs text-muted-foreground">{selected.candidateId}</span>
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-[1fr_9rem] gap-2">
            <div className="space-y-1.5">
              <Label htmlFor="propose-rate">Candidate rate (EUR)</Label>
              <Input
                id="propose-rate"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                placeholder="Leave empty to use the rate expectation"
                aria-invalid={!rateValid}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="propose-unit">Unit</Label>
              <SimpleSelect
                id="propose-unit"
                value={unit}
                options={enumOptions(RateUnit)}
                onChange={(v) => v && setUnit(v as RateUnitCode)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!selected || !rateValid} loading={propose.isPending}>
              Propose
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
